import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  Stack,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useAgentReport } from '../../../../contexts/AgentReportContext';

dayjs.extend(isoWeek);

const Attendance = ({ agentId }) => {
  const theme = useTheme();
  const {fetchSingleAgentAttendanceSummary, fetchSingleAgentAttendanceList } = useAgentReport();

  const [attendanceSummary, setAttendanceSummary] = useState({
    totalDays: 0,
    presentDays: 0,
    presentRate: 0,
    lateDays: 0,
    lateRate: 0,
    totalHours: 0,
    avgHours: 0
  });
  const [attendanceData, setAttendanceData] = useState([]);
  const [pagination, setPagination] = useState({
    total_count: 0,
    current_page: 1,
    page_size: 10,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('This Month');
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(dayjs());
  const [customEndDate, setCustomEndDate] = useState(dayjs());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Date range state
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    return {
      startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  });

  useEffect(() => {
    fetchAttendanceSummaryData();
    fetchAttendanceListData();
  }, [agentId, dateRange.startDate, dateRange.endDate, currentPage, pageSize]);

  // Calculate date range based on filter
  const calculateDateRange = (filter) => {
    const today = dayjs();
    let startDate, endDate;

    switch (filter) {
      case 'Today':
        startDate = today.startOf('day').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'This Week':
        startDate = today.startOf('isoWeek').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'This Month':
        startDate = today.startOf('month').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'This Quarter':
        const currentQuarter = Math.floor(today.month() / 3);
        const quarterStartMonth = currentQuarter * 3;
        startDate = today.month(quarterStartMonth).startOf('month').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
      case 'Custom':
        // Don't update if Custom is selected - wait for dialog confirmation
        return dateRange;
      default:
        startDate = today.startOf('month').format('YYYY-MM-DD');
        endDate = today.endOf('day').format('YYYY-MM-DD');
        break;
    }

    return { startDate, endDate };
  };

  // Get formatted date range display
  const getDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return '';
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    
    // If same day, show single date
    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      return start.format('MMM DD, YYYY');
    }
    
    // Show date range
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  };

  // Handle date filter change
  const handleDateFilterChange = (event) => {
    const selectedFilter = event.target.value;
    
    if (selectedFilter === 'Custom') {
      // If Custom is already selected, just open the dialog (don't change state)
      if (dateFilter === 'Custom') {
        // Set default custom dates to current date range or current month
        const today = dayjs();
        if (dateRange.startDate && dateRange.endDate) {
          setCustomStartDate(dayjs(dateRange.startDate));
          setCustomEndDate(dayjs(dateRange.endDate));
        } else {
          setCustomStartDate(today.startOf('month'));
          setCustomEndDate(today.endOf('day'));
        }
        setCustomDateDialogOpen(true);
        return;
      }
      
      // Update the filter state to Custom
      setDateFilter('Custom');
      
      // Set default custom dates to current date range or current month
      const today = dayjs();
      if (dateRange.startDate && dateRange.endDate) {
        setCustomStartDate(dayjs(dateRange.startDate));
        setCustomEndDate(dayjs(dateRange.endDate));
      } else {
        setCustomStartDate(today.startOf('month'));
        setCustomEndDate(today.endOf('day'));
      }
      
      // Open dialog immediately
      setCustomDateDialogOpen(true);
    } else {
      setDateFilter(selectedFilter);
      const newRange = calculateDateRange(selectedFilter);
      setDateRange(newRange);
    }
  };

  // Handle custom date apply
  const handleCustomDateApply = () => {
    const newRange = {
      startDate: customStartDate.format('YYYY-MM-DD'),
      endDate: customEndDate.format('YYYY-MM-DD')
    };
    setDateRange(newRange);
    setDateFilter('Custom'); // Set filter to Custom after applying
    setCustomDateDialogOpen(false);
  };

  // Handle custom date dialog close
  const handleCustomDateDialogClose = (event, reason) => {
    // Don't close on backdrop click if no dates are selected yet
    if (reason === 'backdropClick') {
      return;
    }
    
    setCustomDateDialogOpen(false);
    
    // If dialog was closed without applying and no valid date range exists, revert filter
    if (reason !== 'backdropClick' && (!dateRange.startDate || !dateRange.endDate || dateFilter === 'Custom')) {
      // Only revert if this is a cancel action and no dates were set
      const hasValidRange = dateRange.startDate && dateRange.endDate && 
                           dayjs(dateRange.startDate).isValid() && 
                           dayjs(dateRange.endDate).isValid();
      if (!hasValidRange) {
        setDateFilter('This Month');
        const newRange = calculateDateRange('This Month');
        setDateRange(newRange);
      }
    }
  };

  const fetchAttendanceSummaryData = async () => {
    setIsLoadingSummary(true);
    try {
      const summary = await fetchSingleAgentAttendanceSummary(agentId, dateRange.startDate, dateRange.endDate);
      setAttendanceSummary(summary);
      setError(null);
    } catch (error) {
      setError(error);
      console.error('Error fetching attendance summary:', error);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const fetchAttendanceListData = async () => {
    setLoadingList(true);
    try {
      const response = await fetchSingleAgentAttendanceList(agentId, 'this_month', dateRange.startDate, dateRange.endDate, currentPage, pageSize);
      
      let attendanceRecords = [];
      if (response && response.attendance) {
        attendanceRecords = response.attendance;
        setAttendanceData(attendanceRecords);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        // Handle legacy response format
        attendanceRecords = Array.isArray(response) ? response : [];
        setAttendanceData(attendanceRecords);
        setPagination({
          total_count: attendanceRecords.length,
          current_page: currentPage,
          page_size: pageSize,
          total_pages: 1,
          has_next: false,
          has_previous: false
        });
      }
      
    } catch (error) {
      setError(error);
      console.error('Error fetching attendance list:', error);
    } finally {
      setLoadingList(false);
    }
  };  





  const getStatusColor = (status) => {
    const statusColors = {
      'Present': 'success',
      'Late': 'warning',
      'Absent': 'error',
      'Half Day': 'info',
    };
    return statusColors[status] || 'default';
  };

  const getProductivityColor = (productivity) => {
    const productivityColors = {
      'High': 'success',
      'Medium': 'warning',
      'Low': 'error',
    };
    return productivityColors[productivity] || 'default';
  };

  // Convert decimal hours to hours and minutes format (e.g., 0.76 -> "0h 46m")
  const formatHoursToHrMin = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '0h 0m';
    
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    
    if (hours === 0 && minutes === 0) return '0h 0m';
    if (hours === 0) return `0h ${minutes}m`;
    if (minutes === 0) return `${hours}h 0m`;
    return `${hours}h ${minutes}m`;
  };

  const calculateBreakHours = (record) => {
    if (!record.check_in_time || !record.check_out_time) return '0h 0m';
    
    // Parse time strings (assuming format like "03:36 PM")
    const checkInTime = parseTimeString(record.check_in_time);
    const checkOutTime = parseTimeString(record.check_out_time);
    
    if (!checkInTime || !checkOutTime) return '0h 0m';
    
    // Calculate total time span in hours
    const totalTimeSpan = (checkOutTime - checkInTime) / (1000 * 60 * 60);
    
    // Break hours = Total time span - Working hours
    const breakHours = Math.max(0, totalTimeSpan - (record.total_hours || 0));
    
    return formatHoursToHrMin(breakHours);
  };

  const parseTimeString = (timeStr) => {
    if (!timeStr) return null;
    
    try {
      // Handle format like "03:36 PM" or "11:50 PM"
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':');
      
      let hour24 = parseInt(hours);
      if (period === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      // Create a date object for today with the parsed time
      const today = new Date();
      today.setHours(hour24, parseInt(minutes), 0, 0);
      
      return today;
    } catch (error) {
      console.error('Error parsing time string:', timeStr, error);
      return null;
    }
  };

  const getStatusFromHours = (totalHours) => {
    if (totalHours >= 8) return 'Present';
    if (totalHours >= 4) return 'Half Day';
    if (totalHours > 0) return 'Partial';
    return 'Absent';
  };



  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  if (isLoadingSummary && loadingList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error loading attendance data: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Attendance Summary Cards with Tabs */}
      <Paper sx={{ 
        borderRadius: 3, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
        mb: 3,
        p: 3
      }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Attendance Summary
            </Typography>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={handleDateFilterChange}
              >
                <MenuItem value="Today">
                  <Box>
                    <Typography variant="body2">Today</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {dayjs().format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="This Week">
                  <Box>
                    <Typography variant="body2">This Week</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {`${dayjs().startOf('isoWeek').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="This Month">
                  <Box>
                    <Typography variant="body2">This Month</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {`${dayjs().startOf('month').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="This Quarter">
                  <Box>
                    <Typography variant="body2">This Quarter</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {(() => {
                        const currentQuarter = Math.floor(dayjs().month() / 3);
                        const quarterStartMonth = currentQuarter * 3;
                        const quarterStart = dayjs().month(quarterStartMonth).startOf('month');
                        return `${quarterStart.format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                      })()}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem 
                  value="Custom"
                  data-value="Custom"
                  onClick={(e) => {
                    e.stopPropagation();
                    // If Custom is already selected, open the dialog again
                    if (dateFilter === 'Custom') {
                      // Prevent Select from closing
                      e.preventDefault();
                      // Set default custom dates to current date range or current month
                      const today = dayjs();
                      if (dateRange.startDate && dateRange.endDate) {
                        setCustomStartDate(dayjs(dateRange.startDate));
                        setCustomEndDate(dayjs(dateRange.endDate));
                      } else {
                        setCustomStartDate(today.startOf('month'));
                        setCustomEndDate(today.endOf('day'));
                      }
                      setCustomDateDialogOpen(true);
                    }
                  }}
                >
                  <Box>
                    <Typography variant="body2">Custom</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {dateFilter === 'Custom' && getDateRangeDisplay() ? getDateRangeDisplay() : 'Select date range'}
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ScheduleIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Total Days
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {attendanceSummary.totalDays}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {attendanceSummary.dateRange ? 
                (() => {
                  const start = dayjs(attendanceSummary.dateRange.start_date);
                  const end = dayjs(attendanceSummary.dateRange.end_date);
                  if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
                    return start.format('MMM DD, YYYY');
                  }
                  return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
                })() 
                : (getDateRangeDisplay() || 'Select date range')
              }
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ color: 'success.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Present Days
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
              {attendanceSummary.presentDays}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {attendanceSummary.presentRate}% Attendance
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Late Days
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {attendanceSummary.lateDays}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {attendanceSummary.lateRate}% Late Rate
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ color: 'info.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Avg Hours
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
              {attendanceSummary.avgHours}h
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Per Day
            </Typography>
          </Paper>
        </Grid>
        </Grid>
      </Paper>

      {/* Daily Attendance Table */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Daily Attendance Record
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete attendance history with location tracking
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pageSize}
                  label="Per Page"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {loadingList ? (
                'Loading...'
              ) : pagination.total_count > 0 ? (
                `Showing ${((currentPage - 1) * pageSize) + 1}-${Math.min(currentPage * pageSize, pagination.total_count)} of ${pagination.total_count} records`
              ) : (
                'No records found'
              )}
            </Typography>
          </Box>
        </Box>

        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Check In</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Check Out</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Working Hours</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Productivity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceData.length > 0 ? (
                  attendanceData.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {record.date}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {record.check_in_time}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {record.check_out_time}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatHoursToHrMin(record.total_hours)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Break: {calculateBreakHours(record)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusFromHours(record.total_hours)} 
                      color={getStatusColor(getStatusFromHours(record.total_hours))}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={record.productivity?.verbose || 'N/A'} 
                      color={getProductivityColor(record.productivity?.verbose)}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No attendance records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Pagination Controls */}
        {!loadingList && pagination.total_pages > 1 && (
          <Box sx={{ 
            p: 3, 
            borderTop: 1, 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Pagination
              count={pagination.total_pages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="large"
            />
          </Box>
        )}
      </Paper>

      {/* Custom Date Range Dialog */}
      <Dialog 
        open={customDateDialogOpen} 
        onClose={handleCustomDateDialogClose} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => setCustomStartDate(newValue)}
                maxDate={customEndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
                minDate={customStartDate}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateDialogClose}>Cancel</Button>
          <Button onClick={handleCustomDateApply} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance;
